<template>
  <div class="ll-page">
    <!-- Bandeau offre (maquette Figma « Banner Lexroom ») -->
    <a
      class="ll-offer-banner"
      :href="demoUrl"
      @click.prevent="openCalendlyDemo"
      target="_blank"
      rel="noopener"
    >
      Choisissez l’abonnement annuel : 3 mois offerts
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M3.33 8h9.34M8 3.33L12.67 8 8 12.67"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </a>

    <!-- HERO -->
    <section class="ll-hero">
      <div class="ll-lines ll-hero-lines" aria-hidden="true"></div>

      <!-- Page de campagne : pas de navbar, le logo seul et sans lien -->
      <img
        class="ll-topbar-logo"
        src="@/assets/hero-v2/logo-query-lexroom.svg"
        alt="Query Juriste, powered by Lexroom"
        width="133"
        height="80"
      />

      <div class="ll-hero-top">
        <h1 class="ll-h1">L'IA juridique qui comprend le droit</h1>
        <p class="ll-subtitle">
          Le premier agent IA de recherche juridique du marché. Il cherche,
          raisonne et vous livre une note sourcée.
        </p>
      </div>

      <a
        class="ll-btn"
        :href="demoUrl"
        @click.prevent="openCalendlyDemo"
        target="_blank"
        rel="noopener"
      >
        Réserver une démo
        <img
          src="@/assets/landing-lexroom/arrow-right.svg"
          alt=""
          width="16"
          height="16"
        />
      </a>

      <div class="ll-hero-logos">
        <p class="ll-overline">Déjà adopté par les équipes de</p>
        <div class="ll-logo-grid">
          <div v-for="logo in clientLogos" :key="logo.src" class="ll-logo">
            <img
              :src="logo.src"
              :alt="'Logo ' + logo.name"
              :class="{ 'll-logo-img--cover': logo.cover }"
              class="ll-logo-img"
            />
          </div>
        </div>
      </div>

      <!-- Capture produit + deux cartes flottantes (comme le hero de la home) -->
      <div class="ll-showcase">
        <div class="ll-showcase-frame">
          <img
            class="ll-showcase-img"
            src="@/assets/hero-v2/app-screenshot.png"
            alt="Aperçu de Query Juriste"
          />
        </div>
        <img
          class="ll-showcase-card ll-showcase-card--integrations"
          src="@/assets/hero-v2/card-integrations.png"
          alt=""
          aria-hidden="true"
        />
        <img
          class="ll-showcase-card ll-showcase-card--sources"
          src="@/assets/hero-v2/card-sources.png"
          alt=""
          aria-hidden="true"
        />
      </div>
      <div class="ll-hero-fade" aria-hidden="true"></div>
    </section>

    <!-- CLIENTS -->
    <section class="ll-clients">
      <p class="ll-clients-label">
        300 cabinets d'avocats et directions juridiques
      </p>
      <div class="ll-clients-list">
        <div
          v-for="person in clientPeople"
          :key="person.name"
          class="ll-person"
        >
          <img
            class="ll-avatar"
            :src="person.avatar"
            :alt="person.name"
            width="48"
            height="48"
          />
          <div class="ll-person-text">
            <p class="ll-person-name">{{ person.name }}</p>
            <p class="ll-person-role">{{ person.role }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 2 : produit, texte + média -->
    <section class="ll-section ll-split">
      <div class="ll-split-text">
        <p class="ll-eyebrow">Découvrez Query Juriste</p>
        <h2 class="ll-h2">
          Une intelligence artificielle qui raisonne en juriste
        </h2>
        <p class="ll-body">
          Vous posez votre question juridique, Query cherche, trouve, raisonne
          et vous remet un rapport détaillé, sourcé et sur-mesure.
        </p>
        <div class="ll-demo-text">
          <a
            class="ll-btn"
            :href="demoUrl"
            @click.prevent="openCalendlyDemo"
            target="_blank"
            rel="noopener"
          >
            Réserver une démo
            <img
              src="@/assets/landing-lexroom/arrow-right.svg"
              alt=""
              width="16"
              height="16"
            />
          </a>
          <p class="ll-caption">
            20 minutes sur votre dossier avec l'un de nos experts
          </p>
        </div>
      </div>
      <div class="ll-visual ll-split-media">
        <img
          class="ll-visual-img"
          src="@/assets/hero-v2/prompt-box-mini.png"
          alt="Question posée à Query avec deux pièces jointes"
          loading="lazy"
        />
      </div>
    </section>

    <!-- SECTION 3 : produit, centré -->
    <section class="ll-section ll-center">
      <div class="ll-center-head">
        <div class="ll-badges">
          <div
            v-for="badge in securityBadges"
            :key="badge.label"
            class="ll-badge"
          >
            <img :src="badge.icon" alt="" width="24" height="24" />
            <span>{{ badge.label }}</span>
          </div>
        </div>
        <h2 class="ll-h2 ll-h2--lg">
          Raisonnement approfondi, zéro hallucination
        </h2>
        <p class="ll-body">
          Chaque document cité est analysé en profondeur en amont, pour la
          fiabilité qu'exige la pratique du droit.
        </p>
      </div>
      <div class="ll-visual ll-center-media">
        <img
          class="ll-visual-img"
          src="@/assets/landing-lexroom/private-library-fr.png"
          alt="Bibliothèque privée : dossiers et documents importés"
          loading="lazy"
        />
      </div>
      <a
        class="ll-btn"
        :href="demoUrl"
        @click.prevent="openCalendlyDemo"
        target="_blank"
        rel="noopener"
      >
        Réserver une démo
        <img
          src="@/assets/landing-lexroom/arrow-right.svg"
          alt=""
          width="16"
          height="16"
        />
      </a>
    </section>

    <!-- COMPARAISON -->
    <section class="ll-section ll-compare">
      <div class="ll-compare-head">
        <h2 class="ll-h2">Query Juriste face aux IA génériques</h2>
        <p class="ll-body ll-muted">
          Une IA pensée pour le travail juridique, pas pour la conversation.
        </p>
      </div>

      <div class="ll-compare-grid">
        <div class="ll-compare-block ll-compare-block--query">
          <img
            class="ll-compare-logo"
            src="@/assets/hero-v2/logo-query-lexroom.svg"
            alt="Query Juriste, powered by Lexroom"
          />
          <ul class="ll-compare-list">
            <li
              v-for="item in compareQuery"
              :key="item"
              class="ll-compare-item ll-compare-item--query"
            >
              <span
                class="ll-compare-icon ll-compare-icon--check"
                aria-hidden="true"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="#fff" />
                  <path
                    d="M6 10.2l2.6 2.6L14 7.4"
                    stroke="#1c73e7"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              {{ item }}
            </li>
          </ul>
        </div>

        <div class="ll-compare-block ll-compare-block--generic">
          <p class="ll-compare-title">IA génériques</p>
          <ul class="ll-compare-list">
            <li
              v-for="item in compareGeneric"
              :key="item"
              class="ll-compare-item ll-compare-item--generic"
            >
              <span class="ll-compare-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M6.5 6.5l7 7M13.5 6.5l-7 7"
                    stroke="#e5484d"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              {{ item }}
            </li>
          </ul>
        </div>
      </div>

      <a
        class="ll-btn"
        :href="demoUrl"
        @click.prevent="openCalendlyDemo"
        target="_blank"
        rel="noopener"
      >
        Réserver une démo
        <img
          src="@/assets/landing-lexroom/arrow-right.svg"
          alt=""
          width="16"
          height="16"
        />
      </a>
    </section>

    <!-- SÉCURITÉ -->
    <section class="ll-section ll-security">
      <div class="ll-security-head">
        <h2 class="ll-h2 ll-h2--lg">
          Vos données ne quittent jamais l'Union Européenne
        </h2>
        <p class="ll-subtitle">
          Query Juriste est hébergé exclusivement sur des infrastructures
          certifiées en Europe. Vos données et vos dossiers ne sortent jamais du
          territoire européen.
        </p>
      </div>
      <div class="ll-security-grid">
        <div
          v-for="card in securityCards"
          :key="card.title"
          class="ll-security-card"
        >
          <span class="ll-security-icon" aria-hidden="true">
            <img :src="card.icon" alt="" width="24" height="24" />
          </span>
          <p class="ll-security-title">{{ card.title }}</p>
          <p class="ll-body">{{ card.text }}</p>
        </div>
      </div>
      <ul class="ll-certifs">
        <li
          v-for="certif in certifications"
          :key="certif.label"
          class="ll-certif"
        >
          <img :src="certif.icon" alt="" width="28" height="28" />
          <span>
            <strong>{{ certif.label }}</strong>
            {{ certif.detail }}
          </span>
        </li>
      </ul>
    </section>

    <!-- DÉMO -->
    <section class="ll-book">
      <div class="ll-book-inner">
        <p class="ll-overline ll-overline--light">Démo personnalisée</p>
        <h2 class="ll-h2 ll-h2--light">
          Réservez une démo sans engagement avec l'un de nos experts.
        </h2>
        <p class="ll-body ll-body--light">
          Nous travaillons ensemble sur l'un de vos dossiers pour vous montrer
          ce que Query change concrètement dans votre pratique.
        </p>
        <a
          class="ll-btn"
          :href="demoUrl"
          @click.prevent="openCalendlyDemo"
          target="_blank"
          rel="noopener"
        >
          Réserver une démo
          <img
            src="@/assets/landing-lexroom/arrow-right.svg"
            alt=""
            width="16"
            height="16"
          />
        </a>
      </div>
    </section>

    <!-- FAQ -->
    <section class="ll-section ll-faq">
      <div class="ll-faq-head">
        <p class="ll-eyebrow">FAQ</p>
        <h2 class="ll-h2">Questions fréquentes</h2>
      </div>
      <div class="ll-faq-list">
        <div
          v-for="(item, i) in faqs"
          :key="item.q"
          class="ll-faq-item"
          :class="{ 'is-open': openFaq === i }"
        >
          <button
            class="ll-faq-question"
            type="button"
            :aria-expanded="openFaq === i ? 'true' : 'false'"
            @click="toggleFaq(i)"
          >
            <span>{{ item.q }}</span>
            <svg
              class="ll-faq-chevron"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 7.5l5 5 5-5"
                stroke="#1a1a1c"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <p v-show="openFaq === i" class="ll-body ll-faq-answer">
            {{ item.a }}
          </p>
        </div>
      </div>
    </section>

    <!-- Pas de footer complet (distraction) : seulement les liens légaux -->
    <footer class="ll-legal">
      <span>© {{ year }} Query Juriste</span>
      <a href="/mentionsPage">Mentions légales</a>
      <a href="/politique-donnees">Politique de confidentialité</a>
    </footer>
  </div>
</template>

<script>
import { openCalendlyDemo, CALENDLY_DEMO_URL } from "@/utils/calendly";
import logoVeilJourde from "@/assets/landing-lexroom/logo-veil-jourde.png";
import logoBg2v from "@/assets/landing-lexroom/logo-bg2v.png";
import logoDePardieu from "@/assets/landing-lexroom/logo-de-pardieu.png";
import logoBredinPrat from "@/assets/landing-lexroom/logo-bredin-prat.png";
import iconGdprSvg from "@/assets/landing-lexroom/icon-gdpr.svg";
import iconShieldSvg from "@/assets/landing-lexroom/icon-shield.svg";
import iconCloudEuSvg from "@/assets/landing-lexroom/icon-cloud-eu.svg";
import iconCertifiedSvg from "@/assets/landing-lexroom/icon-certified.svg";
import iconShieldBlueSvg from "@/assets/hero-v2/icon-shield.svg";
import avatarJustineFontana from "@/assets/landing-lexroom/avatar-justine-fontana.jpg";
import avatarFannyAdoue from "@/assets/landing-lexroom/avatar-fanny-adoue.jpg";
import avatarAnneHortenseJoulie from "@/assets/landing-lexroom/avatar-anne-hortense-joulie.jpg";

export default {
  name: "LandingLexroomPage",
  data() {
    return {
      demoUrl: CALENDLY_DEMO_URL,
      year: new Date().getFullYear(),
      openFaq: 0,
      clientLogos: [
        { name: "Veil Jourde", src: logoVeilJourde },
        { name: "BG2V", src: logoBg2v },
        { name: "De Pardieu", src: logoDePardieu, cover: true },
        { name: "Bredin Prat", src: logoBredinPrat },
      ],
      securityBadges: [
        { label: "Conformité RGPD", icon: iconGdprSvg },
        { label: "Chiffrement AES-256", icon: iconShieldSvg },
        { label: "Hébergement 100% UE", icon: iconCloudEuSvg },
      ],
      clientPeople: [
        {
          name: "Justine Fontana",
          role: "Droit de la Famille & Droit des personnes",
          avatar: avatarJustineFontana,
        },
        {
          name: "Fanny Adoue",
          role: "Droit du numérique & de la Propriété Intellectuelle",
          avatar: avatarFannyAdoue,
        },
        {
          name: "Anne-Hortense Joulie",
          role: "Avocate au Barreau de Paris",
          avatar: avatarAnneHortenseJoulie,
        },
      ],
      compareQuery: [
        "Sources officielles, toujours citées",
        "Aucun entraînement, aucune rétention",
        "Sécurité et hébergement européen",
        "Conçu pour le travail juridique",
      ],
      compareGeneric: [
        "Invente des détails et des sources",
        "Vos données entraînent le modèle",
        "Ni certification ni sécurité",
        "Réponses génériques et imprécises",
      ],
      securityCards: [
        {
          title: "Chiffrement AES-256",
          icon: iconShieldBlueSvg,
          text: "Toutes vos données sont chiffrées au repos avec AES-256 et en transit avec TLS 1.3.",
        },
        {
          title: "Hébergement 100% UE",
          icon: iconShieldBlueSvg,
          text: "Infrastructure Scaleway, datacenters France et Europe de l'Ouest. Aucun transfert hors Union Européenne, contractuellement garanti.",
        },
        {
          title: "Conformité RGPD",
          icon: iconShieldBlueSvg,
          text: "Traitement des données conforme au RGPD. Vos données ne sont jamais utilisées pour entraîner des modèles d'IA tiers.",
        },
        {
          title: "Isolation des dossiers",
          icon: iconShieldBlueSvg,
          text: "Chaque cabinet dispose d'un espace totalement isolé. Vos dossiers et requêtes ne sont jamais partagés entre organisations.",
        },
      ],
      // Uniquement ce qui est vérifiable : l'ISO 27001 est celle de
      // l'hébergeur (Scaleway), pas une certification de Query Juriste.
      certifications: [
        { label: "RGPD", detail: "Conforme", icon: iconGdprSvg },
        {
          label: "ISO 27001",
          detail: "Hébergeur certifié",
          icon: iconCertifiedSvg,
        },
        {
          label: "Hébergé en France",
          detail: "Scaleway",
          icon: iconCloudEuSvg,
        },
        {
          label: "Zéro entraînement",
          detail: "sur vos données",
          icon: iconShieldSvg,
        },
      ],
      faqs: [
        {
          q: "Qu'est-ce que Query Juriste ?",
          a: "Une IA de recherche juridique approfondie : plutôt qu'une réponse instantanée, Query mène une recherche en profondeur, raisonne comme un juriste et produit une note structurée, nuancée et rigoureusement sourcée.",
        },
        {
          q: "Sur quelles sources Query s'appuie-t-il ?",
          a: "L'ensemble des codes, la jurisprudence en Open Data (plus de 2,7 millions de décisions judiciaires et administratives), la jurisprudence de la CJUE, les règlements et directives européens, ainsi que des sources complémentaires par spécialité.",
        },
        {
          q: "Mes données servent-elles à entraîner des modèles ?",
          a: "Non. Query ne réutilise aucune donnée utilisateur pour l'entraînement de ses modèles, et toutes les données sont hébergées en France chez Scaleway.",
        },
      ],
    };
  },
  methods: {
    openCalendlyDemo,
    toggleFaq(i) {
      this.openFaq = this.openFaq === i ? null : i;
    },
  },
  metaInfo() {
    return {
      title: "Query Juriste : l'IA juridique qui comprend le droit",
      meta: [
        {
          name: "description",
          content:
            "Query Juriste, l'IA de recherche juridique qui raisonne comme un juriste. Réservez une démo sans engagement avec l'un de nos experts.",
        },
        // Page de destination publicitaire : on ne la veut pas dans l'index Google
        { name: "robots", content: "noindex, follow" },
      ],
      link: [
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossorigin: "",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Instrument+Serif&family=Inter:wght@400;500;600&display=swap",
        },
      ],
    };
  },
};
</script>

<style scoped>
/* Tokens : charte Lexroom (maquette Figma « QueryJuriste--Landing-Lexroom ») */
.ll-page {
  --gray-00: #ffffff;
  --gray-100: #dbdbdc;
  --gray-200: #bababc;
  --gray-600: #545458;
  --gray-700: #3f3f43;
  --gray-800: #2c2c2f;
  --gray-900: #1a1a1c;
  --primary: #1c73e7;
  --primary-hover: #1562c9;
  --blue-700: #0f4c9d;
  --blue-900: #031e46;
  --lavender: #edf0fe;
  --slate-25: #f7fbff;
  --slate-100: #e4eaf3;
  --navy: #031e46;
  --font-sans:
    "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto,
    sans-serif;
  --font-display: "Instrument Serif", "Times New Roman", Georgia, serif;

  background: var(--gray-00);
  color: var(--gray-900);
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  overflow-x: clip;
}

.ll-page p,
.ll-page h1,
.ll-page h2,
.ll-page ul,
.ll-page figure,
.ll-page blockquote {
  margin: 0;
}

/* ---------- Typographie ---------- */
.ll-h1 {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 52px;
  line-height: 1.2;
  letter-spacing: -0.005em;
  color: var(--gray-900);
}

.ll-h2 {
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 33px;
  line-height: 42.9px;
  letter-spacing: -0.66px;
  color: var(--gray-900);
}

.ll-h2--lg {
  font-size: 39px;
  line-height: 47px;
}

.ll-h2--light {
  color: var(--gray-00);
}

.ll-subtitle {
  font-size: 18px;
  line-height: 27px;
  letter-spacing: -0.09px;
  color: var(--gray-600);
  text-wrap: balance;
}

.ll-body {
  font-size: 16px;
  line-height: 24px;
  color: var(--gray-900);
}

.ll-body--light {
  color: rgba(255, 255, 255, 0.85);
}

.ll-muted {
  color: var(--gray-600);
}

.ll-caption {
  font-size: 14px;
  line-height: 21px;
  color: var(--gray-600);
}

.ll-overline {
  font-size: 13px;
  line-height: 18.2px;
  letter-spacing: 0.39px;
  text-transform: uppercase;
  color: var(--gray-600);
}

.ll-overline--light {
  color: rgba(255, 255, 255, 0.8);
}

.ll-eyebrow {
  font-size: 12px;
  line-height: 16.8px;
  font-weight: 400;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--primary);
}

/* ---------- Bouton ---------- */
.ll-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 99px;
  background: var(--primary);
  color: var(--gray-00);
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 14px;
  line-height: 18.2px;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;
}

.ll-btn:hover {
  background: var(--primary-hover);
  color: var(--gray-00);
  transform: translateY(-1px);
}

.ll-btn:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}

/* ---------- Bandeau offre ---------- */
.ll-offer-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--blue-700);
  color: #fff;
  font-size: 13px;
  line-height: normal;
  text-align: center;
  text-decoration: none;
  transition: background-color 0.2s ease;
}

.ll-offer-banner:hover {
  background: #0c3f84;
  color: #fff;
}

/* ---------- Lignes verticales de fond ---------- */
.ll-lines {
  position: absolute;
  left: 50%;
  width: 1440px;
  transform: translateX(-50%);
  background-image: url("~@/assets/landing-lexroom/line.svg");
  background-size: 20px 100%;
  background-repeat: space no-repeat;
  pointer-events: none;
}

/* ---------- Hero ---------- */
.ll-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  padding: 80px 20px 0;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    var(--slate-25) 40%,
    var(--blue-700) 100%
  );
}

.ll-hero-lines {
  top: 0;
  bottom: 0;
  opacity: 0.3;
}

.ll-hero > *:not(.ll-lines):not(.ll-hero-fade) {
  position: relative;
}

.ll-topbar-logo {
  display: block;
  width: 133px;
  height: 80px;
}

/* Capture produit + cartes flottantes (même composition que la home) */
.ll-showcase {
  width: 100%;
  max-width: 1280px;
  height: 622px;
}

.ll-showcase-frame {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(1000px, 100%);
  height: 100%;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.ll-showcase-img {
  display: block;
  width: 100%;
  aspect-ratio: 994 / 614;
  object-fit: cover;
  object-position: top;
  border-radius: 2px;
  box-shadow: 0 8px 20px rgba(3, 30, 70, 0.3);
}

.ll-showcase-card {
  position: absolute;
  display: block;
}

.ll-showcase-card--integrations {
  top: 160px;
  right: 27px;
  width: 196px;
  height: 267px;
  object-fit: contain;
}

.ll-showcase-card--sources {
  top: 220px;
  left: 0;
  width: 321px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.ll-hero-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  height: 180px;
  background: linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

.ll-hero-top {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 864px;
  text-align: center;
}

.ll-hero-logos {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 20px;
}

.ll-logo-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 20px;
  max-width: 864px;
}

.ll-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
}

.ll-logo-img {
  width: 116px;
  height: 46px;
  object-fit: contain;
}

.ll-logo-img--cover {
  object-fit: cover;
  border-radius: 4px;
}

/* Visuel produit sur carte en dégradé bleu (sections 2 et 3) */
.ll-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  padding: 22px 12px;
  border-radius: 8px;
  background: linear-gradient(180deg, #d2dcfe 0%, #7ea3fb 100%);
}

.ll-visual-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ll-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.ll-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px;
  border-radius: 12px;
  background: var(--lavender);
  color: var(--blue-700);
  font-size: 12px;
  line-height: 16.8px;
  letter-spacing: -0.06px;
  white-space: nowrap;
}

/* ---------- Clients ---------- */
.ll-clients {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 48px;
  row-gap: 12px;
  align-items: center;
  padding: 60px 80px;
  background: linear-gradient(180deg, var(--slate-25) 0%, #fff 50%);
}

.ll-clients-label {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
}

.ll-clients-list {
  display: contents;
}

.ll-person {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ll-avatar {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  object-fit: cover;
  background: var(--gray-00);
}

.ll-person-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.ll-person-name {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: var(--gray-900);
}

.ll-person-role {
  font-size: 16px;
  line-height: 24px;
  color: var(--gray-900);
}

/* ---------- Sections génériques ---------- */
.ll-section {
  padding: 80px;
  max-width: 1440px;
  margin: 0 auto;
}

/* Section 2 */
.ll-split {
  display: flex;
  align-items: center;
  gap: 40px;
}

.ll-split-text {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 0 0 496px;
}

.ll-demo-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding-top: 20px;
}

.ll-split-media {
  flex: 1 1 auto;
  min-width: 0;
}

.ll-split-text .ll-body {
  color: var(--gray-700);
}

/* Section 3 */
.ll-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
}

.ll-center-head,
.ll-compare-head,
.ll-faq-head {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 600px;
  text-align: center;
}

.ll-center-head .ll-badges {
  margin-bottom: 16px;
}

.ll-center-media {
  width: 100%;
  max-width: 800px;
}

/* ---------- Comparaison ---------- */
.ll-compare {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
}

.ll-compare-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
}

.ll-compare-block {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 40px;
}

.ll-compare-block--query {
  border-radius: 4px;
  background: linear-gradient(180deg, var(--primary) 0%, var(--blue-700) 100%);
  box-shadow: 0 16px 40px rgba(15, 76, 157, 0.3);
}

/* Logo Query powered by Lexroom passé en blanc sur le fond bleu */
.ll-compare-logo {
  width: 133px;
  height: 80px;
  object-fit: contain;
  object-position: left center;
  filter: brightness(0) invert(1);
}

.ll-compare-title {
  font-weight: 500;
  font-size: 32px;
  line-height: 43px;
  letter-spacing: -0.02em;
  color: var(--gray-900);
}

.ll-compare-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 0;
  list-style: none;
}

.ll-compare-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-radius: 6px;
  font-size: 18px;
  line-height: 25px;
}

.ll-compare-item--query {
  background: rgba(255, 255, 255, 0.14);
  color: var(--gray-00);
}

.ll-compare-item--generic {
  background: #f1f2f6;
  color: var(--gray-800);
}

.ll-compare-icon {
  display: inline-flex;
  flex-shrink: 0;
}

/* ---------- Sécurité ---------- */
.ll-security {
  display: flex;
  flex-direction: column;
  gap: 60px;
  max-width: none;
  background: linear-gradient(180deg, var(--lavender) 0%, #fff 100%);
}

.ll-security > * {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
}

.ll-security-head {
  display: flex;
  justify-content: space-between;
  gap: 100px;
}

.ll-security-head > * {
  flex: 1 1 0;
}

.ll-security-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.ll-security-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 20px;
  border: 1px solid var(--slate-100);
  border-radius: 8px;
  background: var(--gray-00);
}

.ll-security-card .ll-body {
  font-size: 15px;
  color: var(--gray-600);
}

.ll-security-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--lavender);
}

.ll-security-icon img {
  width: 28px;
  height: 28px;
}

.ll-security-title {
  font-weight: 500;
  font-size: 18px;
  line-height: 26px;
  color: var(--gray-900);
}

.ll-certifs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 40px;
  padding: 24px 0 0;
  border-top: 1px solid var(--gray-100);
  list-style: none;
}

.ll-certif {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  line-height: 18px;
  color: var(--gray-600);
}

.ll-certif strong {
  display: block;
  font-weight: 600;
  font-size: 15px;
  color: var(--navy);
}

/* ---------- Mentions légales ---------- */
.ll-legal {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 24px;
  padding: 40px 20px;
  background: var(--lavender);
  font-size: 13px;
  line-height: 18px;
  color: var(--gray-600);
}

.ll-legal a {
  color: var(--gray-600);
  text-decoration: underline;
}

.ll-legal a:hover {
  color: var(--gray-900);
}

/* ---------- Bloc démo ---------- */
.ll-book {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 584px;
  padding: 80px 20px;
  /* Photo et voile de la maquette Figma (noir 20 % + dégradé bleu nuit) */
  background:
    linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
    linear-gradient(
      180deg,
      rgba(9, 56, 119, 0.2) 0%,
      rgba(1, 16, 43, 0.6) 100%
    ),
    url("~@/assets/landing-lexroom/handshake.jpg") center / cover no-repeat;
}

.ll-book-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 720px;
  text-align: center;
}

.ll-book-inner .ll-btn {
  margin-top: 24px;
}

/* ---------- FAQ ---------- */
.ll-faq {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
}

.ll-faq-head {
  max-width: 500px;
  gap: 12px;
}

.ll-faq-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 640px;
}

.ll-faq-item {
  padding: 20px;
  border-radius: 4px;
  background: var(--slate-25);
}

.ll-faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 16px;
  line-height: 26px;
  color: var(--gray-900);
  text-align: left;
  cursor: pointer;
}

.ll-faq-chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.ll-faq-item.is-open .ll-faq-chevron {
  transform: rotate(180deg);
}

.ll-faq-answer {
  margin-top: 8px;
  color: var(--gray-600);
}

/* ---------- Tablette ---------- */
@media (max-width: 1100px) {
  .ll-section {
    padding: 80px 40px;
  }

  .ll-clients {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 60px 40px;
  }

  .ll-clients-label {
    grid-column: 1 / -1;
    text-align: center;
  }

  .ll-split {
    flex-direction: column;
    align-items: stretch;
  }

  .ll-split-text {
    flex-basis: auto;
  }

  .ll-security-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ll-showcase-card--integrations {
    right: 0;
    width: 160px;
    height: 218px;
  }

  .ll-showcase-card--sources {
    width: 260px;
  }
}

/* ---------- Mobile (maquette 400 px) ---------- */
@media (max-width: 768px) {
  .ll-h1 {
    font-size: 44px;
  }

  .ll-h2,
  .ll-h2--lg {
    font-size: 30px;
    line-height: 36px;
  }

  .ll-hero {
    gap: 32px;
    padding: 48px 20px 0;
  }

  .ll-showcase {
    height: auto;
  }

  .ll-showcase-frame {
    position: relative;
    left: auto;
    transform: none;
    height: auto;
  }

  .ll-showcase-card {
    display: none;
  }

  .ll-hero-fade {
    height: 120px;
  }

  .ll-visual {
    height: auto;
    aspect-ratio: 4 / 3;
  }

  .ll-logo-grid {
    gap: 0 8px;
  }

  .ll-section {
    padding: 80px 20px;
  }

  .ll-clients {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 40px;
    padding: 60px 0;
  }

  .ll-clients-label {
    padding: 0 20px;
    text-align: left;
  }

  .ll-clients-list {
    display: flex;
    gap: 24px;
    padding: 0 20px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  }

  .ll-clients-list::-webkit-scrollbar {
    display: none;
  }

  .ll-person {
    flex: 0 0 272px;
    scroll-snap-align: start;
  }

  .ll-split-text .ll-demo-text {
    padding-top: 8px;
  }

  .ll-certifs {
    justify-content: flex-start;
  }

  .ll-compare {
    gap: 40px;
  }

  .ll-compare-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .ll-compare-block {
    gap: 24px;
    padding: 24px 20px;
  }

  .ll-compare-item {
    font-size: 16px;
    line-height: 22px;
    padding: 14px 16px;
  }

  .ll-security-head {
    flex-direction: column;
    gap: 24px;
  }

  .ll-security-grid {
    grid-template-columns: 1fr;
  }

  .ll-book {
    min-height: 448px;
  }
}
</style>
